import axios from 'axios';
import React, { useState, useEffect, useCallback } from 'react';
import { useVoting } from '../../../../contexts/VotingContext';
import { IBallotNominationView, IVoterNominationView, IApiResponsePayloadModel } from '../../../../types/voteTypes';
import { ErrorBanner } from '../../../common/ErrorBanner';
import { LoadingPanel } from '../../../common/LoadingPanel';
import { SuccessBanner } from '../../../common/SuccessBanner';
import { Nomination } from './Nomination';

interface NominationsProps {
    config: IBallotNominationView
}

const emptyNomination = (): IVoterNominationView => ({
    ballotId: 0,
    custom1: '',
    custom2: '',
    isAccepted: false,
    nominationId: undefined,
    nomineeAddress: '',
    nomineeBio: '',
    nomineeCategory: '',
    nomineeEmail: '',
    nomineeGroup: '',
    nomineeName: '',
    nomineePhoneMobile: '',
    nomineePhoneWork: '',
    nomineePhotoId: '',
    nomineePosition: '',
    seconderEmail: '',
    seconderName: '',
    voterGuid: '',
});

export const Nominations: React.FC<NominationsProps> = ({ config }) => {
    const [isLoading, setLoading] = useState(false)
    const [nomination, setNomination] = useState<IVoterNominationView | null>(null);
    const [nominations, setNominations] = useState<IVoterNominationView[]>([]);
    const [nominationError, setNominationError] = useState<string | null>(null)
    const [submittedNomination, setSubmittedNomination] = useState<string | null>(null);

    const { voter, ballot, previewId } = useVoting();

    const getNominations = useCallback(() => {
        if (!voter || !ballot) return;
        setLoading(true);

        const urlParams = new URLSearchParams(window.location.search);
        const nominationId = urlParams.get('nominationId');
        const acceptanceId = urlParams.get('acceptanceId');

        axios.post<IApiResponsePayloadModel<IVoterNominationView[]>>('/vote/nominations', {
            id: voter.uniqueId,
            key: voter.guid,
            personalId: voter.personalId,
            ballotId: ballot.id,
            previewId,
            acceptanceId,
            nominationId: nominationId ?? 0
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    var nominations: IVoterNominationView[] = response.data.payload;

                    if (nominations.length === 0) {
                        showEmptyNomination();
                    }
                    else if (acceptanceId || nominationId || !config.isMultipleNominations) {
                        setNomination(nominations[0]);
                    }
                    else {
                        setNominations(nominations);
                    }
                }
                else {
                    setNominationError(response.data.errorMessage)
                }

                setLoading(false)
            })
            .catch(function (error) {
                alert(error)
            })
    }, [voter, ballot, config]);

    const getNomination = (nominationId: number) => {
        if (!voter || !ballot) return;
        setLoading(true);
        setSubmittedNomination(null);
        axios.post<IApiResponsePayloadModel<IVoterNominationView>>('/vote/nomination', {
            id: voter.uniqueId,
            key: voter.guid,
            personalId: voter.personalId,
            ballotId: ballot.id,
            nominationId
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    var nomination: IVoterNominationView = response.data.payload;
                    setNomination(nomination);
                }
                else {
                    setNominationError(response.data.errorMessage)
                }

                setLoading(false)
            })
            .catch(function (error) {
                alert(error)
            })
    };

    const onBack = () => {
        setNomination(null);
        getNominations();
    }

    const onSubmitted = (nominationName: string) => {
        setSubmittedNomination(nominationName);
        refresh();
    }

    const refresh = () => {
        setNomination(null);
        getNominations();
    }

    const showEmptyNomination = () => {
        setSubmittedNomination(null);
        setNomination(emptyNomination());
    }

    useEffect(() => {
        getNominations();
    }, [config, getNominations]);

    if (!voter || !ballot) return null;

    return <>

        {nominationError && <ErrorBanner isSticky={true}>{nominationError}</ErrorBanner>}

        <div className="mb-4">
            <h2>{voter.name}</h2>
        </div>
        <LoadingPanel className="py-8 px-10 text-center"
            isLoading={isLoading}
            loadingText="" />
        {submittedNomination &&
            <SuccessBanner>Nomination for {submittedNomination} submitted successfully. You may now close the page.</SuccessBanner>
        }
        {!nomination && nominations.length > 0 &&
            <>
                <span>To view or update a previous nomination please select the nominee below:</span>
                <ul className="mt-2 mb-4">
                    {nominations.map((n) => (
                        <li key={n.nominationId}>
                            <button
                                className="text-brand-alt cursor-pointer"
                                onClick={(e) => { e.preventDefault(); getNomination(n.nominationId!); }}>
                                {n.nomineeName}
                            </button>
                        </li>
                    ))}
                </ul>
                <button className="btn"
                    onClick={(e) => { e.preventDefault(); showEmptyNomination(); }}>
                    Add New Nominee</button>
            </>
        }
        {nomination &&
            <Nomination config={config}
                nomination={nomination}
                onChange={setNomination}
                setNominationError={setNominationError}
                onBack={nominations.length > 0 ? onBack : null}
                onSubmitted={onSubmitted}
                refresh={refresh} />
        }
    </>
}